import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Avatar,
  IconButton,
  Divider,
  CircularProgress,
  TextField,
  Button,
} from '@mui/material';
import { Edit, Close } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../services/api';
import { colors } from '../theme/modernTheme';
import { showError } from '../utils/toast';

export default function Profile() {
  const { user: currentUser, refreshUser } = useAuth();
  const [loading] = useState(false); // No API call needed, data comes from context
  const [isEditing, setIsEditing] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    email: '',
  });

  // Check if current user can edit profile (only ADMIN can edit)
  const canEdit = currentUser?.role === 'ADMIN';

  // Initialize form data from AuthContext user (no API call needed)
  useEffect(() => {
    if (currentUser) {
      setFormData({
        name: currentUser.name,
        email: currentUser.email,
      });
    }
  }, [currentUser]);

  const handleSave = async () => {
    // Validate required fields
    const errors = {};
    if (!formData.name || !formData.name.trim()) {
      errors.name = 'Name is required';
    }

    if (!formData.email || !formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please provide a valid email address';
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      // Don't show toast - field errors are displayed below each field
      return;
    }

    try {
      await authAPI.updateUser(currentUser.id, formData);
      setIsEditing(false);
      setFieldErrors({});
      // Refresh user data from API to update cached data in AuthContext
      await refreshUser();
    } catch (err) {
      if (err.error?.code === 'VALIDATION_ERROR') {
        const errors = err.error.details?.field_errors || {};
        setFieldErrors(errors);
        // Don't show toast - field errors are displayed below each field
      } else if (err.error?.code === 'EMAIL_ALREADY_EXISTS') {
        setFieldErrors({
          email: 'A user with this email address already exists',
        });
      } else {
        showError(err.error?.message || err.message || 'Failed to update profile');
      }
    }
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map((word) => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'ADMIN':
        return 'Admin';
      case 'STAFF':
        return 'Staff';
      case 'READ_ONLY':
        return 'Read Only';
      default:
        return role;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!currentUser) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: 'calc(100vh - 100px)',
        p: 3,
      }}
    >
      <Paper
        sx={{
          maxWidth: 600,
          width: '100%',
          p: 4,
          borderRadius: '4px',
          border: '1px solid #E5E7EB',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
        }}
      >
        {/* Header with Edit and Close Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 2 }}>
          {canEdit && (
            <IconButton
              size="small"
              onClick={() => setIsEditing(!isEditing)}
              sx={{
                color: colors.text.primary,
                '&:hover': {
                  color: colors.primary.main,
                  backgroundColor: 'transparent',
                },
              }}
            >
              <Edit sx={{ fontSize: 20 }} />
            </IconButton>
          )}
          <IconButton
            size="small"
            onClick={() => window.history.back()}
            sx={{
              color: '#737B8B',
              '&:hover': {
                color: colors.error.main,
                backgroundColor: 'transparent',
              },
            }}
          >
            <Close sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>

        {/* Avatar and Name Section */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Avatar
            sx={{
              width: 100,
              height: 100,
              fontSize: '2rem',
              fontWeight: 700,
              background: 'linear-gradient(135deg, #5A6ACF 0%, #707FDD 100%)',
              color: 'white',
              mb: 2,
              mx: 'auto',
            }}
          >
            {getInitials(currentUser.name)}
          </Avatar>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: '#000000',
              fontSize: '1.25rem',
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
            }}
          >
            {currentUser.name}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: '#737B8B',
              fontSize: '0.875rem',
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
            }}
          >
            {currentUser.email}
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Profile Fields */}
        <Box sx={{ mb: 3 }}>
          {/* Name Field */}
          <Box sx={{ mb: 3 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 1,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: '#737B8B',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                }}
              >
                Name
              </Typography>
              {!isEditing && (
                <Typography variant="body1" sx={{ color: '#000000', fontSize: '0.875rem' }}>
                  {currentUser.name}
                </Typography>
              )}
            </Box>
            {isEditing && (
              <TextField
                fullWidth
                size="small"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                error={!!fieldErrors.name}
                helperText={fieldErrors.name}
                FormHelperTextProps={{ sx: { marginLeft: 0 } }}
                sx={{ '& .MuiOutlinedInput-input': { fontSize: '0.875rem' } }}
              />
            )}
          </Box>

          {/* Email Field */}
          <Box sx={{ mb: 3 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 1,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: '#737B8B',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                }}
              >
                Email account
              </Typography>
              {!isEditing && (
                <Typography variant="body1" sx={{ color: '#000000', fontSize: '0.875rem' }}>
                  {currentUser.email}
                </Typography>
              )}
            </Box>
            {isEditing && (
              <TextField
                fullWidth
                size="small"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                error={!!fieldErrors.email}
                helperText={fieldErrors.email}
                FormHelperTextProps={{ sx: { marginLeft: 0 } }}
                sx={{ '& .MuiOutlinedInput-input': { fontSize: '0.875rem' } }}
              />
            )}
          </Box>

          {/* Role Field */}
          <Box sx={{ mb: 3 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: '#737B8B',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                }}
              >
                Role
              </Typography>
              <Typography variant="body1" sx={{ color: '#000000', fontSize: '0.875rem' }}>
                {getRoleLabel(currentUser.role)}
              </Typography>
            </Box>
          </Box>

          {/* Status Field */}
          <Box sx={{ mb: 3 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: '#737B8B',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                }}
              >
                Status
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: currentUser.is_active ? '#67BA70' : '#EF4444',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                }}
              >
                {currentUser.is_active ? 'Active' : 'Inactive'}
              </Typography>
            </Box>
          </Box>

          {/* Last Login Field */}
          <Box sx={{ mb: 3 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: '#737B8B',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                }}
              >
                Last Login
              </Typography>
              <Typography variant="body1" sx={{ color: '#000000', fontSize: '0.875rem' }}>
                {formatDate(currentUser.last_login_at)}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Save Button */}
        {isEditing && (
          <Button
            fullWidth
            variant="contained"
            onClick={handleSave}
            sx={{
              mt: 2,
              py: 1.5,
              textTransform: 'none',
              fontSize: '0.875rem',
              fontWeight: 600,
            }}
          >
            Save Changes
          </Button>
        )}
      </Paper>
    </Box>
  );
}
